import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users as UsersIcon, Search, Filter, ChevronLeft, ChevronRight, Mail, User, Calendar, Shield, Eye, EyeOff, Power, X, UserCheck, UserX, AlertTriangle, Lock, Check, Globe, ChevronDown, ChevronUp, Save, Settings as SettingsIcon, ToggleRight } from 'lucide-react';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { isAdmin } from '../services/api';

// ============================================================================
// User List Tab Component
// ============================================================================
const UserListTab = ({ userIsAdmin }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    email: '',
    name: '',
    sn: '',
    status: 'active',
    page: 1,
    page_size: 50
  });
  const [togglingUser, setTogglingUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null });

  useEffect(() => {
    fetchUsers();
  }, [filters.page, filters.page_size]);

  const fetchUsers = async (searchFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const currentFilters = searchFilters || filters;
      const queryParams = new URLSearchParams();

      queryParams.append('page', currentFilters.page);
      queryParams.append('page_size', currentFilters.page_size);

      if (currentFilters.email) queryParams.append('email', currentFilters.email);
      if (currentFilters.name) queryParams.append('name', currentFilters.name);
      if (currentFilters.sn) queryParams.append('sn', currentFilters.sn);
      if (currentFilters.status) queryParams.append('status', currentFilters.status);

      const response = await fetch(`${API_BASE_URL}/analytics/users-list/?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users);
      setPagination(data.pagination);
      setStatistics(data.statistics);

    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    fetchUsers(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      email: '',
      name: '',
      sn: '',
      status: '',
      page: 1,
      page_size: filters.page_size
    };
    setFilters(clearedFilters);
    fetchUsers(clearedFilters);
  };

  const hasActiveFilters = filters.email || filters.name || filters.sn;

  const showToggleConfirmation = (user) => {
    if (!userIsAdmin) return;
    setConfirmDialog({ open: true, user });
  };

  const handleConfirmToggle = async () => {
    const user = confirmDialog.user;
    if (!user) return;

    setConfirmDialog({ open: false, user: null });
    setTogglingUser(user.id);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/users/${user.id}/toggle-status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        const shouldRemoveFromView =
          (filters.status === 'active' && !data.is_active) ||
          (filters.status === 'inactive' && data.is_active);

        if (shouldRemoveFromView) {
          setUsers(users.filter(u => u.id !== user.id));
        } else {
          setUsers(users.map(u =>
            u.id === user.id ? { ...u, is_active: data.is_active } : u
          ));
        }

        setStatistics(prev => ({
          ...prev,
          active_users: data.is_active ? prev.active_users + 1 : prev.active_users - 1,
          inactive_users: data.is_active ? prev.inactive_users - 1 : prev.inactive_users + 1,
        }));
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
    } finally {
      setTogglingUser(null);
    }
  };

  const handleCancelToggle = () => {
    setConfirmDialog({ open: false, user: null });
  };

  const handlePageChange = (newPage) => {
    const newFilters = { ...filters, page: newPage };
    setFilters(newFilters);
    fetchUsers(newFilters);
  };

  const handlePageSizeChange = (newPageSize) => {
    const newFilters = { ...filters, page_size: newPageSize, page: 1 };
    setFilters(newFilters);
    fetchUsers(newFilters);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSocialNetworkBadge = (sn) => {
    if (sn === 'google') {
      return <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20">Google</span>;
    } else if (sn) {
      return <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full border border-blue-500/20">{sn}</span>;
    } else {
      return <span className="px-2 py-1 bg-gray-500/10 text-gray-400 text-xs rounded-full border border-gray-500/20">Direct</span>;
    }
  };

  const getStatusBadge = (user) => {
    if (!user.is_active) {
      return <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20">Inactive</span>;
    } else if (user.is_superuser) {
      return <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full border border-purple-500/20">Superuser</span>;
    } else if (user.is_staff) {
      return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full border border-yellow-500/20">Staff</span>;
    } else {
      return <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20">Active</span>;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 mb-2">Error loading users</p>
          <p className="text-dark-400 text-sm mb-4">{error}</p>
          <button
            onClick={() => fetchUsers()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Toggle and Stats Card */}
      <div className="dashboard-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Status Toggle Buttons */}
          <div className="flex items-center gap-1 bg-dark-900 p-1 rounded-lg">
            <button
              onClick={() => {
                const newFilters = { ...filters, status: '', page: 1 };
                setFilters(newFilters);
                fetchUsers(newFilters);
              }}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                filters.status === ''
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              All Users
            </button>
            <button
              onClick={() => {
                const newFilters = { ...filters, status: 'active', page: 1 };
                setFilters(newFilters);
                fetchUsers(newFilters);
              }}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                filters.status === 'active'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <UserCheck className="w-4 h-4" />
              Active
            </button>
            <button
              onClick={() => {
                const newFilters = { ...filters, status: 'inactive', page: 1 };
                setFilters(newFilters);
                fetchUsers(newFilters);
              }}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                filters.status === 'inactive'
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <UserX className="w-4 h-4" />
              Inactive
            </button>
          </div>

          {/* Statistics */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-blue-400" />
              <span className="text-white font-semibold">{statistics.total_users?.toLocaleString() || 0}</span>
              <span className="text-dark-400 text-sm">Total</span>
            </div>
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">{statistics.active_users?.toLocaleString() || 0}</span>
              <span className="text-dark-400 text-sm">Active</span>
            </div>
            <div className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-red-400" />
              <span className="text-white font-semibold">{statistics.inactive_users?.toLocaleString() || 0}</span>
              <span className="text-dark-400 text-sm">Inactive</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-400" />
              <span className="text-white font-semibold">{statistics.google_users?.toLocaleString() || 0}</span>
              <span className="text-dark-400 text-sm">Google</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Pill */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          <span className="text-dark-400 text-sm">Active filters:</span>
          {filters.email && (
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full flex items-center gap-1">
              Email: {filters.email}
              <X className="w-3 h-3 cursor-pointer" onClick={() => { const f = { ...filters, email: '', page: 1 }; setFilters(f); fetchUsers(f); }} />
            </span>
          )}
          {filters.name && (
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full flex items-center gap-1">
              Name: {filters.name}
              <X className="w-3 h-3 cursor-pointer" onClick={() => { const f = { ...filters, name: '', page: 1 }; setFilters(f); fetchUsers(f); }} />
            </span>
          )}
          {filters.sn && (
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full flex items-center gap-1">
              Type: {filters.sn === 'google' ? 'Google' : 'Direct'}
              <X className="w-3 h-3 cursor-pointer" onClick={() => { const f = { ...filters, sn: '', page: 1 }; setFilters(f); fetchUsers(f); }} />
            </span>
          )}
          <button
            onClick={handleClearFilters}
            className="px-3 py-1 bg-dark-700 text-dark-300 text-sm rounded-full hover:bg-dark-600 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="dashboard-card">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">Filters & Search</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">Email Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" />
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={filters.email}
                  onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">Name Search</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">User Type</label>
              <select
                value={filters.sn}
                onChange={(e) => setFilters({ ...filters, sn: e.target.value })}
                className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              >
                <option value="">All Types</option>
                <option value="google">Google Users</option>
                <option value="non-google">Direct Users</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">Page Size</label>
              <select
                value={filters.page_size}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={250}>250 per page</option>
                <option value={500}>500 per page</option>
                <option value={1000}>1000 per page</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <Search className="w-4 h-4" />
              <span>Search</span>
            </button>
            <button
              onClick={handleClearFilters}
              className="px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">
            Users ({pagination.total_count?.toLocaleString() || 0})
          </h3>
          {pagination.total_count > 0 && (
            <div className="text-sm text-dark-400">
              Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count}
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-16 bg-dark-700 rounded animate-pulse"></div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="w-12 h-12 text-dark-500 mx-auto mb-4" />
            <p className="text-dark-300">No users found</p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="mt-2 text-primary-400 hover:text-primary-300"
              >
                Clear filters to see all users
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">User</th>
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">Subscriptions</th>
                  <th className="text-left py-3 px-4 text-dark-300 font-medium">Last Login</th>
                  {userIsAdmin && <th className="text-left py-3 px-4 text-dark-300 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-dark-800 hover:bg-dark-800/50">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.first_name ? user.first_name[0].toUpperCase() : (user.email ? user.email[0].toUpperCase() : 'U')}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {user.first_name || user.last_name ?
                              `${user.first_name || ''} ${user.last_name || ''}`.trim() :
                              user.email || `User ${user.id}`
                            }
                          </p>
                          <p className="text-dark-400 text-sm">ID: {user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-dark-200">{user.email || 'No email'}</span>
                    </td>
                    <td className="py-4 px-4">
                      {getSocialNetworkBadge(user.sn)}
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(user)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col space-y-1">
                        {user.subscribe_wnews && (
                          <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full border border-blue-500/20 w-fit">News</span>
                        )}
                        {user.subscribe_promotions && (
                          <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20 w-fit">Promos</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-dark-200 text-sm">{formatDate(user.last_login)}</span>
                    </td>
                    {userIsAdmin && (
                      <td className="py-4 px-4">
                        <button
                          onClick={() => showToggleConfirmation(user)}
                          disabled={togglingUser === user.id}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                            user.is_active
                              ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20'
                              : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20'
                          } ${togglingUser === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          {togglingUser === user.id ? (
                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-dark-700">
            <div className="text-sm text-dark-400">
              Page {pagination.page} of {pagination.total_pages}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={!pagination.has_previous}
                className="px-3 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                First
              </button>

              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_previous}
                className="p-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-4 py-2 bg-primary-600 text-white rounded-lg">
                {pagination.page}
              </span>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="p-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => handlePageChange(pagination.total_pages)}
                disabled={!pagination.has_next}
                className="px-3 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.open && confirmDialog.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 max-w-md w-full mx-4 border border-dark-600 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                confirmDialog.user.is_active
                  ? 'bg-red-500/20'
                  : 'bg-green-500/20'
              }`}>
                <AlertTriangle className={`w-6 h-6 ${
                  confirmDialog.user.is_active
                    ? 'text-red-400'
                    : 'text-green-400'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {confirmDialog.user.is_active ? 'Deactivate User?' : 'Activate User?'}
                </h3>
                <p className="text-dark-400 text-sm">This action can be reversed</p>
              </div>
            </div>

            <div className="bg-dark-700/50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {confirmDialog.user.first_name
                    ? confirmDialog.user.first_name[0].toUpperCase()
                    : (confirmDialog.user.email ? confirmDialog.user.email[0].toUpperCase() : 'U')}
                </div>
                <div>
                  <p className="text-white font-medium">
                    {confirmDialog.user.first_name || confirmDialog.user.last_name
                      ? `${confirmDialog.user.first_name || ''} ${confirmDialog.user.last_name || ''}`.trim()
                      : confirmDialog.user.email || `User ${confirmDialog.user.id}`}
                  </p>
                  <p className="text-dark-400 text-sm">{confirmDialog.user.email}</p>
                </div>
              </div>
            </div>

            <p className="text-dark-300 text-sm mb-6">
              {confirmDialog.user.is_active
                ? 'This user will no longer be able to access the platform. They will be logged out immediately.'
                : 'This user will regain access to the platform and can log in again.'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCancelToggle}
                className="flex-1 px-4 py-2.5 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmToggle}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  confirmDialog.user.is_active
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <Power className="w-4 h-4" />
                {confirmDialog.user.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Market Permissions Tab Component
// ============================================================================
const MarketPermissionsTab = ({ userIsAdmin }) => {
  const [users, setUsers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [permissions, setPermissions] = useState({});
  // Per-symbol shorts toggle state, keyed by symbol_id.
  const [shortsAllowed, setShortsAllowed] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [expandedMarkets, setExpandedMarkets] = useState({});

  // Fetch users and symbols on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch permissions when user is selected. Reset state synchronously and
  // guard against an out-of-order response overwriting the new user's data
  // (race condition: switching A→B fast can let A's slow response land
  // after B's, blanking B's permissions).
  useEffect(() => {
    if (selectedUserId) {
      setPermissions({});
      setShortsAllowed({});
      setError(null);
      setSavedAt(null);
      let cancelled = false;
      fetchUserPermissions(selectedUserId, () => cancelled);
      return () => { cancelled = true; };
    }
  }, [selectedUserId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // Fetch users and symbols in parallel
      const [usersRes, symbolsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/analytics/users-list/?page_size=1000&status=active`, {
          headers: { 'Authorization': `Basic ${token}` }
        }),
        fetch(`${API_BASE_URL}/analytics/symbols/?page_size=100&is_active=all`, {
          headers: { 'Authorization': `Basic ${token}` }
        })
      ]);

      if (!usersRes.ok || !symbolsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const usersData = await usersRes.json();
      const symbolsData = await symbolsRes.json();

      // Only show active users in permissions dropdown
      const activeUsers = (usersData.users || []).filter(u => u.is_active);
      setUsers(activeUsers);

      // Handle paginated response from DRF ViewSet (returns {results: [...]})
      const symbolsList = symbolsData.results || symbolsData || [];
      setSymbols(symbolsList);

      // Group symbols by market
      const marketGroups = {};
      symbolsList.forEach(symbol => {
        const marketName = symbol.market_name || 'unknown';
        if (!marketGroups[marketName]) {
          marketGroups[marketName] = {
            id: symbol.market,
            name: marketName,
            symbols: []
          };
        }
        marketGroups[marketName].symbols.push(symbol);
      });

      // Sort markets in desired order: India, US, International, Metals, Crypto
      const marketOrder = ['india_stocks_indexes', 'us_stocks_indexes', 'international_stocks_indexes', 'precious_metals', 'crypto'];
      const sortedMarkets = Object.values(marketGroups).sort((a, b) => {
        const indexA = marketOrder.indexOf(a.name);
        const indexB = marketOrder.indexOf(b.name);
        // If not in order list, put at end
        const orderA = indexA === -1 ? 999 : indexA;
        const orderB = indexB === -1 ? 999 : indexB;
        return orderA - orderB;
      });
      setMarkets(sortedMarkets);

      // Expand all markets by default
      const expanded = {};
      Object.keys(marketGroups).forEach(market => {
        expanded[market] = true;
      });
      setExpandedMarkets(expanded);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async (userId, isCancelled = () => false) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/analytics/user-permissions/${userId}/`, {
        headers: { 'Authorization': `Basic ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const data = await res.json();
      if (isCancelled()) return; // newer fetch is in-flight, drop this response

      // Build maps keyed by symbol_id from server response.
      const permMap = {};
      const shortsMap = {};
      (data.permissions || []).forEach(perm => {
        permMap[perm.symbol] = true;
        shortsMap[perm.symbol] = !!perm.shorts_allowed;
      });

      setPermissions(permMap);
      setShortsAllowed(shortsMap);
    } catch (err) {
      if (isCancelled()) return;
      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions for this user');
    }
  };

  // Auto-save: persist current grant + shorts state to the backend.
  // Optimistic state must already be applied before calling this.
  // On failure, the caller should revert local state.
  const persistPermissions = async (newPerms, newShorts) => {
    if (!selectedUserId) return false;
    setSaving(true);
    setError(null);
    try {
      const grantedSymbolIds = Object.entries(newPerms)
        .filter(([_, granted]) => granted)
        .map(([id, _]) => parseInt(id));
      const shortsMap = {};
      grantedSymbolIds.forEach(id => {
        if (newShorts[id]) shortsMap[id] = true;
      });
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/analytics/user-permissions/${selectedUserId}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ symbol_ids: grantedSymbolIds, shorts_map: shortsMap }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body && body.error ? `: ${body.error}` : '';
        } catch (_) { /* non-JSON */ }
        throw new Error(`Save failed (HTTP ${res.status}${detail})`);
      }
      setSavedAt(Date.now());
      return true;
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError(err.message || 'Failed to save permissions');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Optimistic apply + auto-save + revert on failure. All toggles funnel
  // through this so each click is its own atomic save (no Save button).
  const applyAndPersist = async (nextPerms, nextShorts) => {
    const prevPerms = permissions;
    const prevShorts = shortsAllowed;
    setPermissions(nextPerms);
    setShortsAllowed(nextShorts);
    const ok = await persistPermissions(nextPerms, nextShorts);
    if (!ok) {
      setPermissions(prevPerms);
      setShortsAllowed(prevShorts);
    }
  };

  const toggleSymbol = (symbolId) => {
    if (!userIsAdmin || saving) return;
    const nextGranted = !permissions[symbolId];
    const nextPerms = { ...permissions, [symbolId]: nextGranted };
    const nextShorts = { ...shortsAllowed };
    if (!nextGranted) delete nextShorts[symbolId];
    applyAndPersist(nextPerms, nextShorts);
  };

  const toggleSymbolShort = (symbolId) => {
    if (!userIsAdmin || saving) return;
    if (!permissions[symbolId]) return;
    const nextShorts = { ...shortsAllowed, [symbolId]: !shortsAllowed[symbolId] };
    applyAndPersist(permissions, nextShorts);
  };

  const toggleMarket = (marketSymbols, grant) => {
    if (!userIsAdmin || saving) return;
    const nextPerms = { ...permissions };
    const nextShorts = { ...shortsAllowed };
    marketSymbols.forEach(s => {
      nextPerms[s.id] = grant;
      if (!grant) delete nextShorts[s.id];
    });
    applyAndPersist(nextPerms, nextShorts);
  };

  const toggleMarketShorts = (marketSymbols, enable) => {
    if (!userIsAdmin || saving) return;
    const nextShorts = { ...shortsAllowed };
    marketSymbols.forEach(s => {
      if (enable) {
        if (permissions[s.id]) nextShorts[s.id] = true;
      } else {
        nextShorts[s.id] = false;
      }
    });
    applyAndPersist(permissions, nextShorts);
  };

  const toggleExpandMarket = (marketName) => {
    setExpandedMarkets(prev => ({
      ...prev,
      [marketName]: !prev[marketName]
    }));
  };

  const getMarketStats = (marketSymbols) => {
    const total = marketSymbols.length;
    const granted = marketSymbols.filter(s => permissions[s.id]).length;
    const grantedSymbols = marketSymbols.filter(s => permissions[s.id]);
    const shortsOn = grantedSymbols.filter(s => shortsAllowed[s.id]).length;
    return {
      total,
      granted,
      allGranted: granted === total,
      noneGranted: granted === 0,
      grantedCount: grantedSymbols.length,
      shortsOn,
      allShortsOn: grantedSymbols.length > 0 && shortsOn === grantedSymbols.length,
      noShortsOn: shortsOn === 0,
    };
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* User Selector */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-orange-400" />
            <h3 className="text-lg font-semibold text-white">Market Access Permissions</h3>
          </div>
          <div className="flex items-center gap-2">
            {saving && (
              <span className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-300 text-sm rounded-full">
                <span className="w-3 h-3 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
                Saving…
              </span>
            )}
            {!saving && savedAt && (
              <span className="px-3 py-1 bg-green-500/20 text-green-300 text-sm rounded-full">
                Saved
              </span>
            )}
          </div>
        </div>
        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">Select User</label>
            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
            >
              <option value="">-- Select a user --</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.email} {user.first_name ? `(${user.first_name} ${user.last_name || ''})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <div className="flex items-center space-x-3 p-3 bg-dark-700/50 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                {selectedUser.first_name ? selectedUser.first_name[0].toUpperCase() : selectedUser.email[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium truncate">
                  {selectedUser.first_name ? `${selectedUser.first_name} ${selectedUser.last_name || ''}` : selectedUser.email}
                </p>
                <p className="text-dark-400 text-sm truncate">{selectedUser.email}</p>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Permissions Grid */}
      {selectedUserId && (
        <div className="dashboard-card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Symbol Permissions</h3>
            <div className="flex items-center space-x-2">
              <span className="text-dark-400 text-sm">
                {Object.values(permissions).filter(Boolean).length} of {symbols.length} symbols
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {markets.map(market => {
              const stats = getMarketStats(market.symbols);
              const isExpanded = expandedMarkets[market.name];

              return (
                <div key={market.name} className="border border-dark-600 rounded-lg overflow-hidden">
                  {/* Market Header */}
                  <div
                    className="flex items-center justify-between p-4 bg-dark-700/50 cursor-pointer hover:bg-dark-700"
                    onClick={() => toggleExpandMarket(market.name)}
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-dark-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-dark-400" />
                      )}
                      <Globe className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium capitalize">{market.name.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        stats.allGranted
                          ? 'bg-green-500/20 text-green-400'
                          : stats.noneGranted
                            ? 'bg-dark-600 text-dark-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {stats.granted}/{stats.total}
                      </span>
                    </div>

                    {userIsAdmin && (
                      <div className="flex items-center gap-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-dark-400 mr-1">Access</span>
                          <button
                            onClick={() => toggleMarket(market.symbols, true)}
                            className="px-3 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                          >
                            Grant All
                          </button>
                          <button
                            onClick={() => toggleMarket(market.symbols, false)}
                            className="px-3 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                          >
                            Revoke All
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-dark-400 mr-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-orange-400" />
                            Shorts ({stats.shortsOn}/{stats.grantedCount})
                          </span>
                          <button
                            onClick={() => toggleMarketShorts(market.symbols, true)}
                            disabled={stats.grantedCount === 0}
                            title={stats.grantedCount === 0 ? 'Grant access to symbols first' : 'Allow shorts on all granted symbols in this market'}
                            className="px-3 py-1 text-xs font-medium bg-orange-500/20 text-orange-300 rounded hover:bg-orange-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Grant All
                          </button>
                          <button
                            onClick={() => toggleMarketShorts(market.symbols, false)}
                            className="px-3 py-1 text-xs font-medium bg-dark-600 text-dark-300 rounded hover:bg-dark-500 transition-colors"
                          >
                            Revoke All
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Symbols Grid */}
                  {isExpanded && (
                    <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {market.symbols.map(symbol => {
                        const isGranted = !!permissions[symbol.id];
                        const shortsOn = !!shortsAllowed[symbol.id];
                        return (
                          <div
                            key={symbol.id}
                            className={`flex items-center justify-between gap-2 p-3 rounded-lg border transition-all ${
                              isGranted
                                ? 'bg-green-500/10 border-green-500/30'
                                : 'bg-dark-800 border-dark-600'
                            } ${!userIsAdmin ? 'opacity-75' : ''}`}
                          >
                            <div
                              onClick={() => userIsAdmin && toggleSymbol(symbol.id)}
                              className={`flex items-center space-x-3 flex-1 min-w-0 ${
                                userIsAdmin ? 'cursor-pointer' : 'cursor-not-allowed'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                isGranted ? 'bg-green-500' : 'bg-dark-600'
                              }`}>
                                {isGranted && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${isGranted ? 'text-green-400' : 'text-white'}`}>
                                  {symbol.name}
                                </p>
                                <p className="text-dark-400 text-xs truncate">{symbol.label}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span
                                className={`text-[10px] font-semibold uppercase tracking-wide ${
                                  shortsOn ? 'text-orange-300' : 'text-dark-400'
                                } ${!isGranted ? 'opacity-40' : ''}`}
                              >
                                Shorts
                              </span>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={shortsOn}
                                aria-label={`Toggle shorts for ${symbol.name}`}
                                disabled={!userIsAdmin || !isGranted}
                                onClick={() => toggleSymbolShort(symbol.id)}
                                title={
                                  !isGranted
                                    ? 'Grant symbol access to allow shorts'
                                    : shortsOn
                                      ? 'Shorts allowed — click to revoke. High-risk feature.'
                                      : 'Allow recommended shorts on this symbol. High-risk feature.'
                                }
                                className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                                  shortsOn ? 'bg-orange-500' : 'bg-dark-600'
                                } ${!userIsAdmin || !isGranted ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                              >
                                <span
                                  className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-transform shadow ${
                                    shortsOn ? 'left-[20px]' : 'left-[2px]'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* Empty State */}
      {!selectedUserId && (
        <div className="dashboard-card">
          <div className="text-center py-12">
            <Lock className="w-12 h-12 text-dark-500 mx-auto mb-4" />
            <p className="text-dark-300">Select a user to manage their market permissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Symbol Management Tab Component
// ============================================================================
const SymbolManagementTab = ({ userIsAdmin }) => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [togglingSymbol, setTogglingSymbol] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, symbol: null });
  const [statistics, setStatistics] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => {
    fetchSymbols();
  }, []);

  const fetchSymbols = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/symbols/?page_size=100&is_active=all`, {
        headers: { 'Authorization': `Basic ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch symbols');

      const data = await response.json();
      const symbolsList = data.results || data || [];

      // Calculate statistics
      const active = symbolsList.filter(s => s.is_active).length;
      setStatistics({ total: symbolsList.length, active, inactive: symbolsList.length - active });

      // Group symbols by market
      const marketGroups = {};
      symbolsList.forEach(symbol => {
        const marketName = symbol.market_name || 'unknown';
        if (!marketGroups[marketName]) {
          marketGroups[marketName] = { name: marketName, symbols: [] };
        }
        marketGroups[marketName].symbols.push(symbol);
      });

      // Sort markets
      const marketOrder = ['india_stocks_indexes', 'us_stocks_indexes', 'international_stocks_indexes', 'precious_metals', 'crypto'];
      const sortedMarkets = Object.values(marketGroups).sort((a, b) => {
        const orderA = marketOrder.indexOf(a.name);
        const orderB = marketOrder.indexOf(b.name);
        return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
      });
      setMarkets(sortedMarkets);

      // Expand all markets by default
      const expanded = {};
      Object.keys(marketGroups).forEach(m => { expanded[m] = true; });
      setExpandedMarkets(expanded);
    } catch (err) {
      console.error('Error fetching symbols:', err);
      setError('Failed to load symbols');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (symbol) => {
    if (symbol.is_active) {
      // Deactivating requires confirmation
      setConfirmDialog({ open: true, symbol });
    } else {
      // Activating doesn't need confirmation
      toggleSymbolStatus(symbol);
    }
  };

  const toggleSymbolStatus = async (symbol) => {
    try {
      setTogglingSymbol(symbol.id);
      setConfirmDialog({ open: false, symbol: null });
      const token = localStorage.getItem('adminToken');

      const response = await fetch(`${API_BASE_URL}/analytics/symbols/${symbol.id}/toggle-status/`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to toggle symbol status');

      const result = await response.json();

      // Update local state
      setMarkets(prev => prev.map(market => ({
        ...market,
        symbols: market.symbols.map(s =>
          s.id === symbol.id ? { ...s, is_active: result.is_active } : s
        )
      })));

      // Update statistics
      setStatistics(prev => ({
        ...prev,
        active: prev.active + (result.is_active ? 1 : -1),
        inactive: prev.inactive + (result.is_active ? -1 : 1)
      }));
    } catch (err) {
      console.error('Error toggling symbol:', err);
      setError('Failed to update symbol status');
    } finally {
      setTogglingSymbol(null);
    }
  };

  const toggleSymbolFrozen = async (symbol) => {
    try {
      setTogglingSymbol(symbol.id);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/symbols/${symbol.id}/toggle-frozen/`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to toggle frozen');
      const result = await response.json();
      setMarkets(prev => prev.map(market => ({
        ...market,
        symbols: market.symbols.map(s =>
          s.id === symbol.id ? { ...s, frozen: result.frozen } : s
        )
      })));
    } catch (err) {
      console.error('Error toggling frozen:', err);
      setError('Failed to update frozen status');
    } finally {
      setTogglingSymbol(null);
    }
  };

  const toggleMarketExpand = (marketName) => {
    setExpandedMarkets(prev => ({ ...prev, [marketName]: !prev[marketName] }));
  };

  const formatMarketName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span className="ml-3 text-dark-300">Loading symbols...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics Bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white">{statistics.total}</div>
          <div className="text-sm text-dark-400">Total Symbols</div>
        </div>
        <div className="bg-dark-800 rounded-lg p-4 border border-green-500/30">
          <div className="text-2xl font-bold text-green-400">{statistics.active}</div>
          <div className="text-sm text-dark-400">Active</div>
        </div>
        <div className="bg-dark-800 rounded-lg p-4 border border-red-500/30">
          <div className="text-2xl font-bold text-red-400">{statistics.inactive}</div>
          <div className="text-sm text-dark-400">Inactive</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start space-x-2">
        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-300">
          Deactivating a symbol hides it globally — from the dashboard, reports, and all users. Only active symbols are visible across the platform.
        </div>
      </div>

      {/* Market Groups */}
      {markets.map(market => {
        const activeCount = market.symbols.filter(s => s.is_active).length;
        const totalCount = market.symbols.length;
        const isExpanded = expandedMarkets[market.name];

        return (
          <div key={market.name} className="bg-dark-800 rounded-lg border border-dark-700 overflow-hidden">
            {/* Market Header */}
            <button
              onClick={() => toggleMarketExpand(market.name)}
              className="w-full flex items-center justify-between p-4 hover:bg-dark-700/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-dark-400" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-dark-400" />
                )}
                <Globe className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">{formatMarketName(market.name)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  activeCount === totalCount
                    ? 'bg-green-500/20 text-green-400'
                    : activeCount === 0
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {activeCount}/{totalCount} active
                </span>
              </div>
            </button>

            {/* Symbols Grid */}
            {isExpanded && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {market.symbols.map(symbol => {
                    const isToggling = togglingSymbol === symbol.id;

                    return (
                      <div
                        key={symbol.id}
                        className={`rounded-lg p-3 border transition-all ${
                          symbol.is_active
                            ? 'bg-dark-700/50 border-green-500/30'
                            : 'bg-dark-900/50 border-dark-600 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium text-sm ${symbol.is_active ? 'text-white' : 'text-dark-400'}`}>
                              {symbol.name}
                            </div>
                            <div className="text-xs text-dark-500 truncate">{symbol.label}</div>
                          </div>
                          {userIsAdmin && (
                            <button
                              onClick={() => handleToggleClick(symbol)}
                              disabled={isToggling}
                              className={`ml-2 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                isToggling ? 'opacity-50 cursor-wait' : ''
                              } ${symbol.is_active ? 'bg-green-500' : 'bg-dark-600'}`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  symbol.is_active ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          )}
                        </div>
                        {userIsAdmin && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-dark-700">
                            <span className={`text-xs ${symbol.frozen ? 'text-amber-400' : 'text-dark-500'}`}>
                              {symbol.frozen ? '🔒 Locked (immutable)' : '🔓 Unlocked'}
                            </span>
                            <button
                              onClick={() => toggleSymbolFrozen(symbol)}
                              disabled={isToggling}
                              title={symbol.frozen ? 'Unlock (allow full recompute)' : 'Lock (freeze transactions)'}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${isToggling ? 'opacity-50 cursor-wait' : ''} ${symbol.frozen ? 'bg-amber-500' : 'bg-dark-600'}`}
                            >
                              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${symbol.frozen ? 'translate-x-4' : 'translate-x-0'}`} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Confirmation Dialog */}
      {confirmDialog.open && confirmDialog.symbol && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl border border-dark-700 p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 rounded-full bg-red-500/20">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Deactivate Symbol</h3>
            </div>
            <p className="text-dark-300 mb-2">
              Are you sure you want to deactivate <span className="text-white font-medium">{confirmDialog.symbol.name}</span>?
            </p>
            <p className="text-sm text-dark-400 mb-6">
              This will hide {confirmDialog.symbol.name} ({confirmDialog.symbol.label}) from the dashboard, reports, and all users globally.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDialog({ open: false, symbol: null })}
                className="px-4 py-2 rounded-lg bg-dark-700 text-dark-300 hover:bg-dark-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => toggleSymbolStatus(confirmDialog.symbol)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Settings Page Component
// ============================================================================
const Settings = () => {
  // URL-driven tab so refresh / bookmark lands on the same view.
  // Use ?tab=user_list | permissions | symbols.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['user_list', 'permissions', 'symbols'];
  const urlTab = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(urlTab) ? urlTab : 'user_list';
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const userIsAdmin = isAdmin();

  const tabs = [
    { id: 'user_list', label: 'User List', icon: UsersIcon },
    { id: 'permissions', label: 'Market Permissions', icon: Lock },
    { id: 'symbols', label: 'Symbol Management', icon: ToggleRight }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <SettingsIcon className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Settings</h1>
          </div>
          <p className="text-dark-300">Manage users, permissions, and symbol configuration</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 bg-dark-800 p-1 rounded-lg w-fit">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'text-dark-300 hover:text-white hover:bg-dark-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'user_list' && <UserListTab userIsAdmin={userIsAdmin} />}
        {activeTab === 'permissions' && <MarketPermissionsTab userIsAdmin={userIsAdmin} />}
        {activeTab === 'symbols' && <SymbolManagementTab userIsAdmin={userIsAdmin} />}
      </div>
    </Layout>
  );
};

export default Settings;
